import { Check } from 'lucide-react';
import { STRINGS } from '@/strings/strings';
import { cn } from '@/lib/utils';

export type Voice = 'male' | 'female';

interface VoiceSelectorProps {
  value: Voice;
  onChange: (value: Voice) => void;
  name?: string;
  /** Override container bg/border classes for world-tinted Builder
   *  routes (e.g. `bg-transparent` on KP to drop the default light-blue
   *  tint that goes nearly-navy on KP's navy page bg). */
  containerClassName?: string;
  /** Override heading text colour (default `text-dream-navy`). */
  headingClassName?: string;
  /** Override the default `border-dream-gold` on the selected-VoiceOption
   *  border (e.g. `border-dream-navy` on SB so gold stays reserved for
   *  the primary "Geschichte erstellen" CTA). */
  selectedBorderClassName?: string;
}

export function VoiceSelector({
  value,
  onChange,
  name = 'voice',
  containerClassName,
  headingClassName,
  selectedBorderClassName,
}: VoiceSelectorProps) {
  return (
    <div className={cn('rounded-xl bg-[rgba(141,192,255,0.18)] p-4', containerClassName)}>
      <h3 className={cn('mb-3 text-sm font-semibold text-dream-navy', headingClassName)}>
        {STRINGS.builder.shared.voiceSelectorHeader}
      </h3>
      <div className="mb-3 grid grid-cols-2 gap-3">
        <VoiceOption
          name={name}
          voice="male"
          selected={value === 'male'}
          label={STRINGS.builder.shared.voiceMale}
          onChange={onChange}
          selectedBorderClassName={selectedBorderClassName}
        />
        <VoiceOption
          name={name}
          voice="female"
          selected={value === 'female'}
          label={STRINGS.builder.shared.voiceFemale}
          onChange={onChange}
          selectedBorderClassName={selectedBorderClassName}
        />
      </div>
    </div>
  );
}

interface VoiceOptionProps {
  name: string;
  voice: Voice;
  selected: boolean;
  label: string;
  onChange: (value: Voice) => void;
  // Required in the inner-component contract (caller may pass undefined).
  // Avoids `exactOptionalPropertyTypes` complaints when the optional prop
  // on the parent flows through to a child that declares it as `?: string`.
  selectedBorderClassName: string | undefined;
}

function VoiceOption({
  name,
  voice,
  selected,
  label,
  onChange,
  selectedBorderClassName,
}: VoiceOptionProps) {
  return (
    <label
      className={cn(
        'flex h-11 cursor-pointer items-center justify-center gap-2 rounded-lg text-base font-medium text-dream-navy transition-colors',
        selected
          ? 'border-2 border-dream-gold bg-dream-white'
          : 'border-[1.5px] border-dream-navy/10 bg-dream-white/80',
        selected && selectedBorderClassName,
      )}
    >
      <input
        type="radio"
        name={name}
        value={voice}
        checked={selected}
        onChange={() => onChange(voice)}
        className="sr-only"
      />
      <Check
        aria-hidden
        strokeWidth={3}
        className={cn('h-5 w-5 shrink-0 text-success', !selected && 'invisible')}
      />
      <span>{label}</span>
    </label>
  );
}
